import type { SequenceDocument } from '../format/types';
import type { OibInstrumentSummary } from './types';

export interface OibReferenceWarning {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

/**
 * Prueft eine Sequenz gegen eine geladene OIB-Instrumentdefinition und meldet
 * Warnungen (keine Fehler) fuer unbekannte oder unpassende Referenzen. Eine
 * Sequenz bleibt fuer sich genommen gueltig, auch wenn kein Instrument
 * geladen ist oder Referenzen nicht passen - siehe OIB_INTEGRATION_CONTRACT.md.
 */
export function checkSequenceAgainstOibInstrument(
  doc: SequenceDocument,
  instrument: OibInstrumentSummary,
): readonly OibReferenceWarning[] {
  const warnings: OibReferenceWarning[] = [];
  const roleById = new Map(instrument.roles.map((role) => [role.id, role]));
  const controlIds = new Set(instrument.controls.map((control) => control.id));
  const stateIds = new Set(instrument.state.map((state) => state.id));
  const sceneIds = new Set(instrument.scenes.map((scene) => scene.id));

  doc.tracks.forEach((track, index) => {
    const role = roleById.get(track.role);
    if (!role) {
      warnings.push({
        path: `/tracks/${index}/role`,
        code: 'oib.unknownRole',
        message: `Rolle "${track.role}" ist in der Instrumentdefinition nicht vorhanden.`,
      });
      return;
    }
    if (role.kind === 'control' && track.type !== 'control') {
      warnings.push({
        path: `/tracks/${index}/role`,
        code: 'oib.roleNotPlayable',
        message: `Rolle "${track.role}" ist eine control-Rolle und kann keine Noten/Akkorde/Drums erzeugen.`,
      });
    }
  });

  doc.events.forEach((event, index) => {
    if (event.kind !== 'control') return;
    if (!controlIds.has(event.controlId)) {
      warnings.push({
        path: `/events/${index}/controlId`,
        code: 'oib.unknownControl',
        message: `Bedienelement "${event.controlId}" ist in der Instrumentdefinition nicht vorhanden.`,
      });
    }
    if (event.stateRef !== undefined && !stateIds.has(event.stateRef)) {
      warnings.push({
        path: `/events/${index}/stateRef`,
        code: 'oib.unknownState',
        message: `Zustand "${event.stateRef}" ist in der Instrumentdefinition nicht vorhanden.`,
      });
    }
    if (event.sceneRef !== undefined && !sceneIds.has(event.sceneRef)) {
      warnings.push({
        path: `/events/${index}/sceneRef`,
        code: 'oib.unknownScene',
        message: `Szene "${event.sceneRef}" ist in der Instrumentdefinition nicht vorhanden.`,
      });
    }
  });

  return warnings;
}
